import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useMatch, useSearchParams } from 'react-router-dom';

import { company } from '~/company/Company';
import { useIntercom } from '~/hooks/useIntercom';
import { ROUTES } from '~/pages/Root/constants';
import { AnalyticsEvent, analyticsService } from '~/services/analytics';

import { orderStore } from '~/stores';

import Icon from '../Icon/Icon';

import { DESKTOP_HEADER_ID } from './constants';
import styles from './HeaderMini.module.scss';

const HeaderMini = React.forwardRef<
  HTMLHeadingElement,
  Omit<React.ComponentPropsWithRef<'header'>, 'id' | 'children'>
>((props, ref) => {
  const { t } = useTranslation();
  const { openMessenger } = useIntercom();
  const [searchParams] = useSearchParams();
  const matchOrderPayment = useMatch(ROUTES.order_payment);

  const isOrderPaymentPage = !!matchOrderPayment;

  const showBackButton = isOrderPaymentPage
    ? searchParams.get('status') !== 'success'
    : true;
  const showSupport = company.hasContactSupport;

  const backText = isOrderPaymentPage
    ? t('backToCheckout')
    : company.match({
        citydrinks: t('backToCart'),
        v3: t('backToCart'),
        default: t('continueShopping'),
      });
  const backLink = isOrderPaymentPage
    ? '/checkout'
    : company.match({
        citydrinks: '/cart',
        v3: '/cart',
        default: '/',
      });

  const handleClickSupport = () => {
    analyticsService.track(AnalyticsEvent.CONTACT_SUPPORT_BUTTON_CLICKED, {
      from: isOrderPaymentPage ? 'order_payment' : 'checkout',
    });
    openMessenger();
  };

  return (
    <header
      {...props}
      ref={ref}
      id={DESKTOP_HEADER_ID}
      className={classes([
        styles.header,
        styles.header_root,
        styles.compactScroll,
      ])}
      data-company={company.name}
    >
      <div className={classes(['content-layout', styles.header_content])}>
        <div className={classes([styles.container, styles.start])}>
          {showBackButton &&
            (isOrderPaymentPage && orderStore.onBackFromPayment ? (
              <button
                className="button _no-color _no-padding"
                onClick={orderStore.onBackFromPayment}
              >
                <Icon type="chevron" size={24} className="icon__rotate-90" />
                <p className={styles.goBack}>{backText}</p>
              </button>
            ) : (
              <Link className="button _no-color _no-padding" to={backLink}>
                <Icon type="chevron" size={24} className="icon__rotate-90" />
                <p className={styles.goBack}>{backText}</p>
              </Link>
            ))}
        </div>
        <div className={classes([styles.container, styles.center])}>
          <Link data-link="header-logo" to="/">
            <img
              src={company.match({
                circlek: company.images.logo.light,
                default: company.images.logo.dark,
              })}
              alt=""
              className={styles.header_logo}
            />
          </Link>
        </div>
        <div className={classes([styles.container, styles.end])}>
          {showSupport && (
            <div className={styles.contacts}>
              <div className={styles.text}>
                <p className={styles.desc}>{t('havingDifficulty')}</p>
                <p className={styles.phone}>{t('askUs')}</p>
              </div>
              <div className={styles.frame} onClick={handleClickSupport}>
                <Icon
                  type="message_contact_us"
                  size={26}
                  className={styles.icon}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
});

export default observer(HeaderMini);
